import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Check, ArrowRight, MapPin } from "lucide-react";
import { useApp, formatEUR } from "@/context/AppContext";
import { VEHICLES } from "@/data/products";
import { isDaily } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function VehicleCard({ vehicle, index }) {
  const { t, lang, addToCart, setCartOpen } = useApp();
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  const handleAdd = () => {
    if (isDaily(vehicle)) return navigate(`/produit/${vehicle.id}`);
    addToCart(vehicle.id, 1);
    toast.success(`${vehicle.name[lang]} — ${t.cta.added}`, {
      action: { label: t.cart.title, onClick: () => setCartOpen(true) },
    });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45 }}
      className="grid grid-cols-1 gap-6 rounded-3xl border border-slate-200 bg-white p-5 md:grid-cols-2 md:p-6"
      data-testid={`fleet-vehicle-${vehicle.id}`}
    >
      {/* Gallery */}
      <div>
        <Link to={`/produit/${vehicle.id}`} className="block aspect-[16/10] overflow-hidden rounded-2xl bg-slate-100" data-testid={`fleet-image-link-${vehicle.id}`}>
          <img src={vehicle.images[active]} alt={vehicle.name[lang]} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" data-testid={`fleet-main-image-${vehicle.id}`} />
        </Link>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {vehicle.images.map((src, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`fleet-thumb-${vehicle.id}-${i}`}
              className={`aspect-[16/10] overflow-hidden rounded-xl border-2 transition-all ${active === i ? "border-cea-accent" : "border-transparent opacity-70 hover:opacity-100"}`}
            >
              <img src={src} alt={`${vehicle.name[lang]} ${i + 1}`} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="flex flex-col">
        <Link to={`/produit/${vehicle.id}`} data-testid={`fleet-detail-link-${vehicle.id}`} className="group/title w-fit">
          <h2 className="text-2xl font-extrabold tracking-tight text-cea-ink transition-colors group-hover/title:text-cea-accent">{vehicle.name[lang]}</h2>
        </Link>
        <p className="mt-1.5 text-slate-500">{vehicle.tagline[lang]}</p>
        {vehicle.city && (
          <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{t.detail.photographed} {vehicle.city}</p>
        )}

        <div className="mt-4">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-cea-accent">{t.fleet.equivalent}</p>
          <p className="mt-1 text-sm font-medium text-slate-600">{vehicle.brands}</p>
        </div>

        <ul className="mt-4 grid grid-cols-2 gap-2">
          {vehicle.specs[lang].map((s, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
              <Check className="h-4 w-4 shrink-0 text-cea-accent" />{s}
            </li>
          ))}
        </ul>

        <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-6">
          <p className="text-2xl font-extrabold text-cea-navy">
            {formatEUR(vehicle.price)}
            <span className="text-xs font-medium text-slate-400">{vehicle.unit === "day" ? t.perDay : ` ${t.forfait}`}</span>
          </p>
          <div className="flex gap-2">
            <Button asChild variant="outline" className="rounded-full border-cea-navy px-5 text-cea-navy hover:bg-slate-50">
              <Link to={`/produit/${vehicle.id}`} data-testid={`fleet-more-${vehicle.id}`}>{t.detail.learnMore}<ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button onClick={handleAdd} data-testid={`fleet-add-${vehicle.id}`} className="rounded-full bg-cea-accent px-6 text-white hover:bg-cea-navy">
              <Plus className="mr-1 h-4 w-4" />{vehicle.unit === "day" ? t.cta.rent : t.cta.addToCart}
            </Button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Fleet() {
  const { t } = useApp();
  return (
    <div className="mx-auto max-w-7xl px-6 py-16 sm:px-8" data-testid="fleet-page">
      <header className="mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cea-accent">{t.nav.fleet}</p>
        <h1 className="mt-3 text-4xl font-extrabold tracking-tighter text-cea-ink sm:text-5xl">{t.fleet.title}</h1>
        <p className="mt-3 max-w-2xl text-slate-500">{t.fleet.sub}</p>
      </header>

      <div className="space-y-6">
        {VEHICLES.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} />)}
      </div>
    </div>
  );
}
